"use client";
import { motion } from "framer-motion";
import {
  Container,
  CountUpValue,
  SectionSubHeading,
  VelocityMarquee,
} from "@/components";
import {
  caseStudyBannerStats,
  caseStudyHeadline,
  caseStudyHeadlineAccent,
  caseStudyLede,
  caseStudyReels,
} from "@/data/CaseStudyPageData";
import { fadeInUp, word, wordStagger } from "@/lib/animation/variant";

// Two display lanes with a thin mono ribbon between them, so the reels read as
// a rhythm rather than three identical bands of the same weight.
const REEL_FRAME = ["", "border-y border-black/10 bg-black/[0.02]", ""];
const REEL_TRACK = [
  "text-[clamp(3rem,10.5vw,8rem)] leading-[0.86] font-black tracking-tighter uppercase text-accent-foreground",
  "py-3.5 font-mono text-[11px] tracking-[0.35em] uppercase text-muted-foreground md:text-sm",
  "text-[clamp(3rem,10.5vw,8rem)] leading-[0.86] font-black tracking-tighter uppercase text-primary",
];

const CaseStudyBanner = () => {
  return (
    <section className="w-full bg-background pt-8 pb-14 md:pb-20">
      <Container className="px-4 lg:px-10">
        <div className="grid gap-7 lg:grid-cols-[1.18fr_0.82fr] lg:gap-15">
          <div className="space-y-2">
            <motion.div
              custom={0}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <SectionSubHeading subtitle="Case studies" />
            </motion.div>

            <motion.h1
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={wordStagger}
              className="flex flex-wrap gap-x-[0.22em] text-4xl leading-[0.86] font-black tracking-tighter uppercase md:text-6xl xl:text-8xl"
            >
              {caseStudyHeadline.map((item, index) => (
                <span
                  key={`${item}-${index}`}
                  className="inline-block overflow-hidden"
                >
                  <motion.span variants={word} className="inline-block">
                    {item}
                  </motion.span>
                </span>
              ))}
              <span className="inline-block overflow-hidden">
                <motion.span
                  variants={word}
                  className="inline-block text-primary"
                >
                  {caseStudyHeadlineAccent}
                </motion.span>
              </span>
            </motion.h1>

            {caseStudyLede.map((paragraph, index) => (
              <motion.p
                key={paragraph.slice(0, 24)}
                custom={index + 1}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                className="max-w-3xl pt-4 text-base leading-relaxed font-medium text-muted-foreground md:text-lg"
              >
                {paragraph}
              </motion.p>
            ))}
          </div>

          <motion.dl
            custom={2}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="grid h-fit grid-cols-2 gap-px border border-black/10 bg-black/10 lg:self-center"
          >
            {caseStudyBannerStats.map((stat) => (
              <div key={stat.value} className="bg-background p-5 md:p-6">
                <dt className="text-2xl leading-none font-black tracking-tighter text-primary tabular-nums md:text-4xl">
                  <CountUpValue value={stat.value} count={stat.count} />
                </dt>
                <dd className="mt-3 font-mono text-[10px] leading-relaxed tracking-[0.16em] text-muted-foreground uppercase">
                  {stat.label}
                </dd>
              </div>
            ))}
          </motion.dl>
        </div>
      </Container>

      {/* Reels reading the page's scroll: they drift on their own, run faster
          while the reader scrolls and reverse when the reader scrolls back.
          Each wipes up on load, one after the other. */}
      <div className="mt-12 md:mt-16">
        {caseStudyReels.map((reel, index) => (
          <motion.div
            key={reel.words}
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{
              duration: 1.1,
              delay: index * 0.12,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            <VelocityMarquee
              speed={reel.speed}
              direction={reel.direction}
              className={REEL_FRAME[index]}
              trackClassName={REEL_TRACK[index]}
            >
              {Array.from({ length: reel.repeat }).map((_, copy) => (
                <span key={copy} className="pr-[0.3em] whitespace-nowrap">
                  {reel.words} ·
                </span>
              ))}
            </VelocityMarquee>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default CaseStudyBanner;
